import React from 'react';
import PropTypes from 'prop-types';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as VisibilityIcon
} from '@mui/icons-material';

const getStatusColor = (status) => {
  switch (status) {
    case 'new':
      return 'primary';
    case 'contacted':
      return 'info';
    case 'quoted':
      return 'warning';
    case 'converted':
      return 'success';
    case 'declined':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'new':
      return 'New';
    case 'contacted':
      return 'Contacted';
    case 'quoted':
      return 'Quoted';
    case 'converted':
      return 'Converted';
    case 'declined':
      return 'Declined';
    default:
      return status;
  }
};

function StationeryEnquiriesTab({ enquiries, loading, error, onViewEnquiry, onConvertToOrder, onCreateOrganization }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="stationery enquiries table">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Organisation</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enquiries.map((enquiry) => (
              <TableRow
                key={enquiry.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{enquiry.createdAt ? formatDateLong(enquiry.createdAt) : 'N/A'}</TableCell>
                <TableCell>{`${enquiry.firstName} ${enquiry.lastName}`}</TableCell>
                <TableCell>{enquiry.schoolOrBusinessName}</TableCell>
                <TableCell>{enquiry.emailAddress}</TableCell>
                <TableCell>{enquiry.phoneNumber}</TableCell>
                <TableCell>
                  <Tooltip title={enquiry.productDescription}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {enquiry.productDescription}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(enquiry.status)}
                    color={getStatusColor(enquiry.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => onViewEnquiry && onViewEnquiry(enquiry)}
                        sx={{ color: 'primary.main' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {enquiries.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No enquiries found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

StationeryEnquiriesTab.propTypes = {
  enquiries: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onViewEnquiry: PropTypes.func,
  onConvertToOrder: PropTypes.func,
  onCreateOrganization: PropTypes.func
};

export default StationeryEnquiriesTab;
